import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { faPen, faTrash, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { formatNoteForCopy } from '@europrocurement/l2d-utils';
import { type ContextMenuState, useClipboard } from '@europrocurement/l2d-hooks';

export type NoteContextMenuProps = {
    contextMenu: ContextMenuState;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
    canEdit: boolean;
    canDelete: boolean;
    noteContent: string;
    createdBy: string;
    createdAt: Date;
    isImportant?: boolean;
};

export const NoteContextMenu: React.FunctionComponent<NoteContextMenuProps> = function ({
    contextMenu,
    onClose,
    onEdit,
    onDelete,
    canEdit,
    canDelete,
    noteContent,
    createdBy,
    createdAt,
    isImportant,
}) {
    const { copyToClipboard } = useClipboard();

    const handleEdit = () => {
        onEdit();
        onClose();
    };

    const handleDelete = () => {
        onDelete();
        onClose();
    };

    const handleCopy = async () => {
        const formattedContent = formatNoteForCopy(noteContent, createdBy, createdAt, isImportant);

        await copyToClipboard(formattedContent, 'Note copiée dans le presse-papiers');
        onClose();
    };

    return (
        <Menu
            open={contextMenu.open}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={{
                top: contextMenu.mouseY,
                left: contextMenu.mouseX,
            }}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: '160px',
                        boxShadow: 3,
                    },
                },
            }}
        >
            {canEdit && (
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon>
                        <FaOptionIcon
                            icon={faPen}
                            sx={{ fontSize: '14px' }}
                        />
                    </ListItemIcon>
                    <ListItemText primary="Éditer la note" />
                </MenuItem>
            )}

            <MenuItem onClick={handleCopy}>
                <ListItemIcon>
                    <FaOptionIcon
                        icon={faCopy}
                        sx={{ fontSize: '14px' }}
                    />
                </ListItemIcon>
                <ListItemText primary="Copier le contenu" />
            </MenuItem>

            {(canEdit || canDelete) && <Divider />}

            {canDelete && (
                <MenuItem
                    onClick={handleDelete}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <FaOptionIcon
                            icon={faTrash}
                            sx={{ fontSize: '14px', color: 'error.main' }}
                        />
                    </ListItemIcon>
                    <ListItemText primary="Supprimer la note" />
                </MenuItem>
            )}
        </Menu>
    );
};
